import { useContext, useMemo } from "react";
import { FormProvider, useForm } from "react-hook-form";
import { useModal } from "react-modal-hook";
import { RESOURCE_NAME } from "common/authorization";
import { OBJECT_TYPE } from "common/constants";
import { getTypedObject } from "common-client/utils/customObject";
import {
  GetAccountActivityDocument,
  GetActivitiesDocument,
  GetSubmissionDocument,
  GetTasksDocument,
  useGetTaskQuery,
  useUpdateTaskMutation,
} from "../../../generated/graphql";
import { useStatusToasts } from "../../../hooks/useStatusToasts";
import { normalizeQueryResult } from "../../../utils/apollo";
import { track } from "../../../utils/tracking";
import { AuthContext } from "../../Authorization/AuthContext";
import { ContentSection } from "../../Common/__styles__/Modal";
import { EmptyState } from "../../Common/EmptyState";
import Loading from "../../Common/Icons/Loading";
import Modal from "../../Common/Modal";
import type { Task } from "../__queries__";
import { Form } from "./Form";
import { LayoutContainer } from "./LayoutContainer";
import { TaskFormData } from "./types";
import { taskToFormData } from "./utils";

type EditTaskFormProps = {
  taskId: string;
  onCancel: () => void;
  onSuccess: () => void;
};

type EditTaskFormInternalProps = Omit<EditTaskFormProps, "taskId"> & {
  task: Task;
};

// There seems to be a bug in the UserSelect component in which quickly
// re-rendering with a different value (in this case, after the task is fetched)
// prevents the value from being properly rendered. Ideally, we'd fix that
// bug—and we should—but another way to prevent that scenario is to only render
// the hydrated task form once the hydration values are available. That's what
// this component does. A side benefit of this is that it forces us to handle
// the loading/error/not found states.
export function EditTaskForm({ taskId, ...rest }: EditTaskFormProps) {
  const getTaskQuery = useGetTaskQuery({ variables: { id: taskId } });
  const { data, status } = normalizeQueryResult(getTaskQuery);

  if (status === "success") {
    if (data.task) {
      const typedObject = getTypedObject(data.task.targetObject);

      return (
        <LayoutContainer
          title="Edit task"
          subtitle={typedObject?.object.label}
          content={<EditTaskFormInternal task={data.task} {...rest} />}
          size="large"
        />
      );
    }

    return (
      <LayoutContainer
        title="Edit task"
        content={
          <ContentSection>
            <EmptyState
              title="Task not found"
              body="This task may have been deleted or no longer exists."
              icon="missing-file"
              paddingTop={0}
            />
          </ContentSection>
        }
      />
    );
  }

  if (status === "pending") {
    return <Loading size="large" />;
  }

  if (status === "error") {
    return (
      <LayoutContainer
        title="Edit task"
        content={
          <ContentSection>
            <EmptyState
              title="An error occurred"
              body="We’re unable to load this task. Please try again."
              icon="error-alert"
              paddingTop={0}
              primaryButtonProps={{
                label: "Retry",
                onClick: () => {
                  void getTaskQuery.refetch();
                },
              }}
            />
          </ContentSection>
        }
      />
    );
  }

  return status satisfies never;
}

export function EditTaskFormInternal({
  task,
  onCancel,
  onSuccess,
}: EditTaskFormInternalProps) {
  const toasts = useStatusToasts();

  // memoized to prevent an infinite loop since taskToFormData produces a new
  // object every time
  const values = useMemo(() => {
    return taskToFormData(task);
  }, [task]);

  const form = useForm<TaskFormData>({
    values,
    resetOptions: {
      keepDirtyValues: true,
    },
  });

  const { authorized } = useContext(AuthContext);

  const canUpdateTask = authorized({
    resource: RESOURCE_NAME.TASK,
    permission: "update",
  });

  const typedObject = getTypedObject(task.targetObject);
  const label = typedObject?.object.label;

  const [updateTask, { loading }] = useUpdateTaskMutation();

  const onSubmit = async (values: TaskFormData) => {
    if (!canUpdateTask) {
      toasts.addWarningToast("You are not authorized to update tasks.");
      throw new Error("User is not authorized to update tasks");
    }

    if (!typedObject) {
      toasts.addErrorToast("Task target object not found.");
      throw new Error("Task target object not found");
    }

    await updateTask({
      variables: {
        data: {
          ...values,
          id: task.id,
          assignees: { users: values.assignees.users },
        },
      },
      onCompleted: () => {
        toasts.addSuccessToast(`Your task at ${label} was updated.`);
        track("Task updated", {
          label,
          [typedObject.type === OBJECT_TYPE.PROPERTY ?
            "propertyId"
          : "customObjectId"]: typedObject.object.id,
        });
        onSuccess();
      },
      onError: () => {
        toasts.addErrorToast(
          `Your task at ${label} was not updated. Try again.`,
        );
      },
      refetchQueries: [
        GetTasksDocument,
        GetSubmissionDocument,
        GetActivitiesDocument,
        GetAccountActivityDocument,
      ],
    });
  };

  const hasRelatedSubmission = Boolean(task.relatedSubmission?.id);

  return (
    <FormProvider {...form}>
      <Form
        submitButtonLabel="Update"
        onCancel={onCancel}
        onSubmit={onSubmit}
        disableAssociatedRecordTypeField={hasRelatedSubmission}
        disabled={!canUpdateTask || loading}
        targetObject={task.targetObject}
      />
    </FormProvider>
  );
}

type UseEditTaskModalParams = {
  taskId: string;
  onSuccess?: () => void;
  onCancel?: () => void;
};

export function useEditTaskModal({
  taskId,
  onSuccess,
  onCancel,
}: UseEditTaskModalParams) {
  const [showModal, hideModal] = useModal(
    () => (
      <Modal onRequestClose={() => hideModal()}>
        <EditTaskForm
          taskId={taskId}
          onCancel={onCancel ?? hideModal}
          onSuccess={onSuccess ?? hideModal}
        />
      </Modal>
    ),
    [taskId, onSuccess, onCancel],
  );

  return [showModal, hideModal] as const;
}
